import React, { MutableRefObject, useEffect, useState } from 'react';
import JourneySlideover from 'src/common/JourneySlideOver';
import delay from 'lodash/delay';
import { DashboardLibraryContentAddPanelLinkInput } from './link-input';
import { DashboardLibraryContentAddPanelSection } from './section';
import { DashboardLibraryContentAddPanelUploadFiles } from './upload-files';
import { SubmitButton } from './submit-button';
import { TagSelectionButton } from './tags-selection-button';

interface Props {
  onSubmit: (data: { url?: string; files?: File[]; tags?: { id: number; name: string }[] }) => void;
  panelOpenRef: MutableRefObject<() => void>;
  panelCloseRef: MutableRefObject<() => void>;
}

const SLIDE_ANIMATION_TIMEOUT_IN_MS = 200;

export const DashboardLibraryContentAddPanel = ({ panelOpenRef, panelCloseRef, onSubmit }: Props) => {
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showSlideOver, setShowSlideOver] = useState(false);
  const [url, setUrl] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<{ id: number; name: string }[]>([]);

  const onSlideOverClosed = () => {
    setShowSlideOver(false);
    delay(() => {
      setShowAddPanel(false);
      setUrl('');
      setFiles([]);
      setTags([]);
    }, SLIDE_ANIMATION_TIMEOUT_IN_MS);
  };

  useEffect(() => {
    panelOpenRef.current = () => {
      setShowAddPanel(true);
      delay(() => {
        setShowSlideOver(true);
      }, SLIDE_ANIMATION_TIMEOUT_IN_MS);
    };

    panelCloseRef.current = onSlideOverClosed;
  }, []);

  const handleSubmit = () => {
    onSubmit({ url, files, tags });
    onSlideOverClosed();
  };

  const showSubmitButton = url.trim() !== '' || files.length > 0;
  const showUrlInput = files.length === 0;
  const showFileUpload = url.trim() === '';

  if (!showAddPanel) {
    return null;
  }

  return (
    <JourneySlideover
      isBedrock
      enableScroll
      title='Add content'
      open={showSlideOver}
      maxWidth='auto'
      containerClassName='pointer-events-none'
      panelContainerClassName='pointer-events-auto'
      panelClassName='w-[368px]'
      titleClassName='font-brand-serif'
      onClose={onSlideOverClosed}
    >
      <div className='mt-2'>
        <div className='flex flex-col gap-y-6'>
          {showUrlInput && (
            <DashboardLibraryContentAddPanelSection
              title='Link'
              subTitle='If your content is a URL paste it below. Some examples are Notion documents, Google Slides, or any website or
          link you want to share with your recipients.'
            >
              <DashboardLibraryContentAddPanelLinkInput onUrlChange={setUrl} />
            </DashboardLibraryContentAddPanelSection>
          )}

          {showUrlInput && showFileUpload && <div className='h-px bg-bedrock-gray-medium w-full rounded-[100px]' />}

          {showFileUpload && (
            <DashboardLibraryContentAddPanelSection
              title='Files'
              subTitle='If your content is a file like a PDF or spreadsheet, upload it.'
            >
              <DashboardLibraryContentAddPanelUploadFiles onSelectFiles={setFiles} />
            </DashboardLibraryContentAddPanelSection>
          )}

          {showSubmitButton && (
            <>
              <TagSelectionButton onTagsSelected={setTags} />
              <SubmitButton onClick={handleSubmit} />
            </>
          )}
        </div>
      </div>
    </JourneySlideover>
  );
};
